import {
  getMyShareCode,
  pairFromZnOrPayload,
  regeneratePairingPayload,
  getOrCreatePairingPayload,
} from '../../app/zionApp.ts'
import { pairingCountdownText } from '../../pairing/pairingPayload.ts'
import { renderQrToCanvas } from '../../pairing/qrCode.ts'
import { t, tf } from '../../i18n/index.ts'
import { applyAppLayout } from '../layout.ts'
import { appendBlocks, copyText, escapeHtml } from '../dom.ts'
import { showSendPairingModal } from '../modals.ts'
import { navigate } from '../router.ts'
import { readZnCodeInput, setZnCodeInput, wireZnCodeInput, znCodeInputHtml } from '../znCodeInput.ts'

let pairTimer: number | null = null

function clearPairTimer(): void {
  if (pairTimer != null) {
    window.clearInterval(pairTimer)
    pairTimer = null
  }
}

function utcMidnightHint(): string {
  const now = new Date()
  const next = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
  const h = Math.floor((next - now.getTime()) / 3_600_000)
  const m = Math.floor(((next - now.getTime()) % 3_600_000) / 60_000)
  return tf('pair_zn_midnight_hint', { h, m })
}

async function copyWithFeedback(text: string, btn: HTMLButtonElement): Promise<void> {
  const prev = btn.textContent
  const ok = await copyText(text)
  btn.textContent = ok ? t('pair_copied') : t('pair_copy_failed')
  window.setTimeout(() => {
    btn.textContent = prev
  }, 1500)
}

export async function renderPairScreen(root: HTMLElement): Promise<void> {
  clearPairTimer()
  applyAppLayout('page')

  const znCode = await getMyShareCode()
  let tab: 'mine' | 'scan' = 'mine'
  let payloadExp = 0
  let payloadText = ''

  root.innerHTML = ''
  appendBlocks(
    root,
    `
      <header class="topbar premium-topbar">
        <button type="button" class="icon-btn" id="btn-back">←</button>
        <h2 class="topbar-title card-title">${escapeHtml(t('pair_title'))}</h2>
      </header>
      <section class="card zn-card zn-hero-card">
        <h2 class="card-title">${escapeHtml(t('pair_zn_title'))}</h2>
        <p class="zn-code zn-code-display" id="zn-display">${escapeHtml(znCode)}</p>
        <p class="status muted small" id="zn-hint">${escapeHtml(utcMidnightHint())}</p>
        <button type="button" class="secondary" id="btn-copy-zn">${escapeHtml(t('pair_btn_copy_zn'))}</button>
      </section>
      <div class="pair-tabs">
        <button type="button" class="pair-tab active" data-tab="mine" id="tab-mine">${escapeHtml(t('pair_tab_mine'))}</button>
        <button type="button" class="pair-tab" data-tab="scan" id="tab-scan">${escapeHtml(t('pair_tab_scan'))}</button>
      </div>
      <div id="pair-panel"></div>
    `,
  )

  const panel = root.querySelector('#pair-panel') as HTMLElement
  const znDisplay = root.querySelector('#zn-display') as HTMLElement

  root.querySelector('#btn-back')!.addEventListener('click', () => navigate('home'))
  root.querySelector('#btn-copy-zn')!.addEventListener('click', () => {
    void copyWithFeedback(znDisplay.textContent ?? '', root.querySelector('#btn-copy-zn') as HTMLButtonElement)
  })

  async function refreshZn(): Promise<void> {
    const code = await getMyShareCode()
    znDisplay.textContent = code
    root.querySelector('#zn-hint')!.textContent = utcMidnightHint()
  }

  async function loadMyQr(): Promise<void> {
    try {
      const { payload, exp } = await getOrCreatePairingPayload()
      payloadText = payload
      payloadExp = exp
      panel.innerHTML = `
      <section class="card pair-qr-card">
        <canvas id="pair-qr" class="pair-qr" aria-label="${escapeHtml(t('pair_qr_aria'))}"></canvas>
        <p class="status muted small">${escapeHtml(t('pair_expires_in'))} <strong id="pair-countdown">${escapeHtml(pairingCountdownText(exp))}</strong></p>
        <p class="pair-payload" id="pair-payload">${escapeHtml(payload)}</p>
        <button type="button" class="secondary" id="btn-copy-payload">${escapeHtml(t('pair_btn_copy_payload'))}</button>
        <button type="button" id="btn-regen-qr">${escapeHtml(t('pair_btn_regen_qr'))}</button>
        <p class="status muted small">${escapeHtml(t('pair_qr_hint'))}</p>
        <p id="pair-qr-error" class="status err" hidden></p>
      </section>
    `
      const canvas = panel.querySelector('#pair-qr') as HTMLCanvasElement
      const errEl = panel.querySelector('#pair-qr-error') as HTMLElement
      try {
        await renderQrToCanvas(canvas, payload)
      } catch (qrErr) {
        console.error('[ZION] pair QR render failed', qrErr)
        if (errEl) {
          errEl.hidden = false
          errEl.textContent =
            qrErr instanceof Error ? qrErr.message : t('pair_qr_read_err')
        }
      }
      panel.querySelector('#btn-copy-payload')!.addEventListener('click', () => {
        void copyWithFeedback(payloadText, panel.querySelector('#btn-copy-payload') as HTMLButtonElement)
      })
      panel.querySelector('#btn-regen-qr')!.addEventListener('click', () => {
        void (async () => {
          try {
            const next = await regeneratePairingPayload()
            payloadText = next.payload
            payloadExp = next.exp
            await renderQrToCanvas(canvas, next.payload)
            panel.querySelector('#pair-payload')!.textContent = next.payload
            panel.querySelector('#pair-countdown')!.textContent = pairingCountdownText(next.exp)
            if (errEl) errEl.hidden = true
          } catch (regenErr) {
            console.error('[ZION] pair QR regen failed', regenErr)
            if (errEl) {
              errEl.hidden = false
              errEl.textContent =
                regenErr instanceof Error ? regenErr.message : t('pair_qr_read_err')
            }
          }
        })()
      })
      clearPairTimer()
      pairTimer = window.setInterval(() => {
        const elCount = panel.querySelector('#pair-countdown')
        if (!elCount) return
        if (Date.now() >= payloadExp) {
          elCount.textContent = t('pair_expired')
          return
        }
        elCount.textContent = pairingCountdownText(payloadExp)
      }, 1000)
    } catch (err) {
      console.error('[ZION] loadMyQr failed', err)
      panel.innerHTML = `
        <section class="card pair-qr-card">
          <p class="status muted">QR non disponibile (rete/socket offline). Puoi tornare alla home e usare ZION senza questo QR.</p>
          <p class="status err small">${escapeHtml(err instanceof Error ? err.message : String(err))}</p>
          <button type="button" id="btn-retry-qr">${escapeHtml(t('pair_btn_regen_qr'))}</button>
          <button type="button" class="secondary" id="btn-pair-skip-home">Salta e torna alla Home</button>
        </section>
      `
      panel.querySelector('#btn-retry-qr')?.addEventListener('click', () => {
        void loadMyQr()
      })
      panel.querySelector('#btn-pair-skip-home')?.addEventListener('click', () => navigate('home'))
    }
  }

  function loadScanPanel(): void {
    clearPairTimer()
    panel.innerHTML = `
      <section class="card pair-card">
        <h2 class="card-title">${escapeHtml(t('pair_contact_zn_title'))}</h2>
        <p class="status muted small">${t('pair_zn_prefix_hint')}</p>
        ${znCodeInputHtml('pair-zn-code')}
        <button type="button" id="btn-pair-zn">${escapeHtml(t('pair_btn_zn_request'))}</button>
        <p id="pair-zn-status" class="status muted"></p>
      </section>
      <section class="card">
        <h2>${escapeHtml(t('pair_paste_title'))}</h2>
        <p class="status muted small">${t('pair_paste_hint')}</p>
        <textarea id="pair-paste" rows="4" placeholder="${escapeHtml(t('pair_paste_placeholder'))}"></textarea>
        <input id="pair-file" type="file" accept="image/*" capture="environment" hidden />
        <div class="row-btns">
          <button type="button" class="secondary" id="btn-pick-qr">${escapeHtml(t('pair_btn_pick_qr'))}</button>
          <button type="button" id="btn-pair-submit">${escapeHtml(t('pair_btn_connect_qr'))}</button>
        </div>
        <p id="pair-status" class="status muted"></p>
      </section>
    `
    const status = panel.querySelector('#pair-status') as HTMLElement
    const znStatus = panel.querySelector('#pair-zn-status') as HTMLElement
    const paste = panel.querySelector('#pair-paste') as HTMLTextAreaElement
    const znInput = panel.querySelector('#pair-zn-code') as HTMLInputElement
    const fileInput = panel.querySelector('#pair-file') as HTMLInputElement
    wireZnCodeInput(znInput)

    panel.querySelector('#btn-pair-zn')!.addEventListener('click', () => {
      void (async () => {
        const code = readZnCodeInput(znInput)
        if (!code || code.length !== 9) {
          znStatus.textContent = t('pair_zn_digits_err')
          znStatus.className = 'status err'
          return
        }
        showSendPairingModal({
          codeLabel: code,
          onConfirm: async () => {
            znStatus.textContent = t('pair_sending')
            znStatus.className = 'status muted'
            const result = await pairFromZnOrPayload(code)
            znStatus.textContent = result.message
            znStatus.className = `status ${result.ok ? 'ok' : 'err'}`
            if (result.ok) setTimeout(() => navigate('home'), 700)
          },
        })
      })()
    })

    panel.querySelector('#btn-pick-qr')!.addEventListener('click', () => fileInput.click())
    fileInput.addEventListener('change', () => {
      void (async () => {
        const file = fileInput.files?.[0]
        if (!file) return
        status.textContent = t('pair_reading_qr')
        status.className = 'status muted'
        try {
          const { default: jsQR } = await import('jsqr')
          const bitmap = await createImageBitmap(file)
          const canvas = document.createElement('canvas')
          canvas.width = bitmap.width
          canvas.height = bitmap.height
          const ctx = canvas.getContext('2d')
          if (!ctx) throw new Error('canvas')
          ctx.drawImage(bitmap, 0, 0)
          const img = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const found = jsQR(img.data, img.width, img.height)
          if (!found?.data) {
            status.textContent = t('pair_qr_not_found')
            status.className = 'status err'
            return
          }
          const data = found.data.trim()
          if (data.toUpperCase().startsWith('ZN-')) {
            setZnCodeInput(znInput, data)
            znStatus.textContent = t('pair_zn_read_ok')
            znStatus.className = 'status ok'
          } else {
            paste.value = data
            status.textContent = t('pair_qr_read_ok')
            status.className = 'status ok'
          }
        } catch {
          status.textContent = t('pair_qr_read_err')
          status.className = 'status err'
        }
      })()
    })

    panel.querySelector('#btn-pair-submit')!.addEventListener('click', () => {
      void (async () => {
        status.textContent = t('pair_sending')
        status.className = 'status muted'
        const result = await pairFromZnOrPayload(paste.value)
        status.textContent = result.message
        status.className = `status ${result.ok ? 'ok' : 'err'}`
        if (result.ok) setTimeout(() => navigate('home'), 700)
      })()
    })
  }

  function setTab(next: 'mine' | 'scan'): void {
    tab = next
    root.querySelectorAll('.pair-tab').forEach((btn) => {
      btn.classList.toggle('active', (btn as HTMLElement).dataset.tab === tab)
    })
    if (tab === 'mine') void loadMyQr()
    else loadScanPanel()
  }

  root.querySelector('#tab-mine')!.addEventListener('click', () => setTab('mine'))
  root.querySelector('#tab-scan')!.addEventListener('click', () => setTab('scan'))

  void refreshZn()
  setTab('mine')
}

export function clearPairScreenHooks(): void {
  clearPairTimer()
}
